import {
  Dispatch,
  Reducer,
  ReducerAction,
  ReducerState,
  createContext,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
} from 'react';
import { z } from 'zod';
import { Text, ProgressIndicator, Flex, Button } from '@applyboard/crystal-ui';
import { match, P } from 'ts-pattern';
import { LightBulbAndGearsIllustration } from '~/illustrations';
import { CheckHifiIcon, DocumentAlertHifiIcon, CircleCrossHifiIcon } from '@applyboard/ui-icons';
import { useTranslation } from '@applyboard/ui-utils';
import { assert } from '~/utils/assert';
import { useContextOrThrow } from '~/utils/hooks';

/**
 * Represents an error that occurs when a user tries to pay for an order that
 * has already been paid for
 */
export class AlreadyPaidError extends Error {
  name = 'AlreadyPaidError' as const;
  message = 'The order has already been paid for';
}

/**
 * Represents an error that occurs when a user tries to pay for an order that
 * has been inactivated or made unnecessary in some way
 */
export class InactiveOrderError extends Error {
  name = 'InactiveOrderError' as const;
}

/**
 * Represents an error that occurs when a user tries to pay for an order and an
 * unknown error occurs
 */
export class UnknownPaymentError extends Error {
  name = 'UnknownPaymentError' as const;
}

type PaymentFrameAction =
  | {
      type: 'order_created';
      orderId: string;
    }
  | {
      type: 'payment_error';
      error: AlreadyPaidError | InactiveOrderError | UnknownPaymentError;
    }
  | {
      type: 'payment_made';
    }
  | {
      type: 'payment_confirmed';
    }
  | {
      type: 'reset';
    };

type PaymentFrameState =
  | {
      status: 'creating_order';
    }
  | {
      status: 'user_paying';
      orderId: string;
    }
  | {
      status: 'confirming_payment';
    }
  | {
      status: 'payment_success';
    }
  | {
      status: 'already_paid';
    }
  | {
      status: 'inactive';
    }
  | {
      status: 'unknown_error';
    };

export type PaymentFrameStatus = PaymentFrameState['status'];

type UseReducer<State, Action> = [
  ReducerState<Reducer<State, Action>>,
  Dispatch<ReducerAction<Reducer<State, Action>>>,
];

const PaymentContext = createContext<UseReducer<PaymentFrameState, PaymentFrameAction> | null>(
  null,
);
PaymentContext.displayName = 'PaymentContext';

/**
 * Renders a payment iframe that allows the user to directly make a payment within our platform
 */
export function PaymentFrame({
  onRequestCreateOrder,
  onRequestConfirmPayment,
  onCancelPayment,
}: {
  /**
   * This function is called when the user tries to create an order.
   * Consumers should create the order and then call the `onSuccess` or
   * `onError` callbacks that are provided
   */
  onRequestCreateOrder: (params: {
    // This callback should be called after you successfully create the order
    onSuccess: (orderId: string) => void;

    // This callback should be called if you run into an error trying to create the order
    onError: (code: AlreadyPaidError | InactiveOrderError | UnknownPaymentError) => void;
  }) => void;

  /**
   * This function is called when the user finishes the payment process and
   * wants to confirm the payment was successful.  Consumers should try to
   * confirm the payment was successful and then call `onSuccess` or `onError`
   */
  onRequestConfirmPayment: (params: {
    // This callback should be called when you successfully confirm the payment was made
    onSuccess: () => void;

    // This callback should be called if there is an error confirming the payment was made
    onError: (err: UnknownPaymentError) => void;
  }) => void;

  /**
   * This function is called when a payment is cancelled
   */
  onCancelPayment: () => void;
}) {
  const { t } = useTranslation('PaymentFrame');
  const [state, dispatch] = useContextOrThrow(PaymentContext);

  const onRequestCreateOrderRef = useRef(onRequestCreateOrder);
  onRequestCreateOrderRef.current = onRequestCreateOrder;

  // eslint-disable-next-line no-restricted-syntax -- TODO: This is a temporary solution because it's complex to avoid the useEffect here.  We should remove this after we add DynamicForm.Trigger
  useEffect(() => {
    onRequestCreateOrderRef.current({
      onSuccess: orderId => {
        dispatch({ type: 'order_created', orderId });
      },
      onError: error => {
        dispatch({ type: 'payment_error', error });
      },
    });
  }, [dispatch]);

  useIframeEvents({
    onPaymentError: error => {
      dispatch({ type: 'payment_error', error });
    },
    onPaymentSuccess: () => {
      dispatch({ type: 'payment_made' });

      onRequestConfirmPayment({
        onSuccess: () => {
          dispatch({ type: 'payment_confirmed' });
        },
        onError: error => {
          dispatch({ type: 'payment_error', error });
        },
      });
    },
    onPaymentCancel: () => {
      onCancelPayment();
    },
  });

  return match(state)
    .with({ status: 'creating_order' }, () => <LoadingState label={t('preparingOrder')} />)
    .with({ status: 'user_paying' }, ({ orderId }) => (
      <>
        <div css={{ height: '500px' }}>
          <iframe
            title="Pay for application"
            src={`${window.location.origin}/orders/${orderId}/pay`}
            height="100%"
            width="100%"
            style={{ border: 0 }}
          />
        </div>
        <Flex gap={2} grow={1} justify="end" pt={6}>
          <Button onClick={onCancelPayment} width="hug" intent="secondary" emphasis="highlighted">
            {t('cancelButtonText')}
          </Button>
        </Flex>
      </>
    ))
    .with({ status: 'confirming_payment' }, () => <LoadingState label={t('processingPayment')} />)
    .with({ status: 'payment_success' }, () => <PaymentSuccessfulState />)
    .with({ status: 'already_paid' }, () => (
      <PaymentWarningState description={t('alreadyPaidMessage')} />
    ))
    .with({ status: 'inactive' }, () => <PaymentWarningState description={t('inactiveMessage')} />)
    .with({ status: 'unknown_error' }, () => <PaymentFailureState />)
    .exhaustive();
}

export function PaymentFrameProvider({ children }: { children: React.ReactNode }) {
  return (
    <PaymentContext.Provider
      value={useReducer(
        (_state: PaymentFrameState, action: PaymentFrameAction) =>
          match(action)
            .returnType<PaymentFrameState>()
            .with({ type: 'order_created' }, ({ orderId }) => ({
              status: 'user_paying' as const,
              orderId,
            }))
            .with({ type: 'payment_made' }, () => ({
              status: 'confirming_payment' as const,
            }))
            .with({ type: 'payment_confirmed' }, () => ({
              status: 'payment_success' as const,
            }))
            .with({ type: 'payment_error', error: P.instanceOf(AlreadyPaidError) }, () => ({
              status: 'already_paid' as const,
            }))
            .with({ type: 'payment_error', error: P.instanceOf(InactiveOrderError) }, () => ({
              status: 'inactive' as const,
            }))
            .with({ type: 'payment_error', error: P.instanceOf(UnknownPaymentError) }, () => ({
              status: 'unknown_error' as const,
            }))
            .with({ type: 'reset' }, () => ({
              status: 'creating_order' as const,
            }))
            .exhaustive(),
        { status: 'creating_order' as const },
      )}
    >
      {children}
    </PaymentContext.Provider>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div
      css={{
        width: '240px',
        maxWidth: '100%',
        margin: 'auto',
      }}
    >
      <Flex gap={6} direction="column" pt={8} pb={24}>
        <Flex justify="center">
          <LightBulbAndGearsIllustration />
        </Flex>
        <ProgressIndicator label={label} variant="linear" indeterminate />
      </Flex>
    </div>
  );
}

function PaymentSuccessfulState() {
  const { t } = useTranslation('PaymentFrame');
  return (
    <Flex gap={4}>
      <CheckHifiIcon intent="positive" />
      <Text>{t('successMessage')}</Text>
    </Flex>
  );
}

function PaymentWarningState({ description }: { description: string }) {
  return (
    <Flex gap={4}>
      <DocumentAlertHifiIcon intent="warning" />
      <Text>{description}</Text>
    </Flex>
  );
}

function PaymentFailureState() {
  const { t } = useTranslation('PaymentFrame');
  return (
    <Flex gap={4}>
      <CircleCrossHifiIcon intent="negative" />
      <Text>{t('unknownErrorMessage')}</Text>
    </Flex>
  );
}

const PaymentMessage = z
  .object({
    // TODO: talk to the payments team about migrating away from `error` / `message` / `errorCode` to `type`. This will make the code cleaner
    type: z.literal('payment_cancelled').nullish(),
    error: z.boolean(),
    message: z.string().optional(),
    errorCode: z.string().optional(),
    orderId: z.string(),
  })
  .transform(data =>
    match(data)
      .with({ type: 'payment_cancelled' }, d => ({ type: d.type }))
      .with({ error: true, errorCode: 'alreadyPaid' }, () => ({ type: 'already_paid' as const }))
      .with({ error: true }, () => ({ type: 'unknown_error' as const }))
      .with({ error: false }, () => ({ type: 'payment_success' as const }))
      .exhaustive(),
  );

type PaymentMessage = z.infer<typeof PaymentMessage>;

export function getPaymentMessageFromEvent(event: MessageEvent<unknown>): null | PaymentMessage {
  if (event.origin !== window.location.origin) {
    return null;
  }

  let data = null;
  try {
    assert(typeof event.data === 'string');
    data = JSON.parse(event.data);
  } catch (err) {
    // do nothing
  }

  const parsedPaymentMessage = PaymentMessage.safeParse(data);
  if (!parsedPaymentMessage.success) {
    return null;
  }

  return parsedPaymentMessage.data;
}

function useIframeEvents(params: {
  onPaymentSuccess: () => void;
  onPaymentError: (error: AlreadyPaidError | UnknownPaymentError) => void;
  onPaymentCancel: () => void;
}) {
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useLayoutEffect(() => {
    const { onPaymentCancel, onPaymentError, onPaymentSuccess } = paramsRef.current;

    const listener = (event: MessageEvent<unknown>) => {
      const message = getPaymentMessageFromEvent(event);

      if (!message) {
        return;
      }

      match(message.type)
        .with('payment_cancelled', () => {
          onPaymentCancel();
        })
        .with('payment_success', () => {
          onPaymentSuccess();
        })
        .with('already_paid', () => {
          onPaymentError(new AlreadyPaidError());
        })
        .with('unknown_error', () => {
          // We unfortunately need to do nothing for unknown payment errors.
          //
          // This is because the payment team currently sends us an empty error
          // code for non-fatal form validation errors, such as "Blank card
          // number". We want to allow the user to retry in that case, and not
          // show a fatal error screen.
          //
          // Because we can't differentiate between fatal and non-fatal errors,
          // it's safest to not show the fatal error screen and just allow the
          // user to retry within the iframe
        })
        .exhaustive();
    };

    window.addEventListener('message', listener, false);

    return () => {
      window.removeEventListener('message', listener, false);
    };
  }, []);
}

export function useResetPaymentFrame() {
  const [, dispatch] = useContextOrThrow(
    PaymentContext,
    'useResetPaymentFrame must be used within a DynamicForm',
  );

  return () => {
    dispatch({ type: 'reset' });
  };
}

export function usePaymentStatus() {
  const [state] = useContextOrThrow(
    PaymentContext,
    'usePaymentStatus must be used within a DynamicForm',
  );
  return state.status;
}
